import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import {
  getTrackingConsentType,
  logTrackingConsent,
} from '../services/apiGateway';

const CONSENT_COOKIE_KEY = 'av_cookie_consent';
const CONSENT_SESSION_STORAGE_KEY = 'av_cookie_session_id';
const GEO_COOKIE_KEY = 'av_geo';
const GEO_COOKIE_MAX_AGE = 60 * 60 * 24 * 30;
const GEO_FETCH_TIMEOUT_MS = 25000;
const VALID_CONSENT_TYPES = new Set(['optIn', 'optOut', 'acknowledgeOnly']);
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;
const DEFAULT_COOKIE_POLICY_URL = 'https://www.av.vc/cookie-policy';
const CONSENT_DECISION_EVENT_NAME_IN_GTM = 'cookie_consent_decision';
const DEFAULT_DECISION_SOURCE = 'userClick';
const GOOGLE_ANALYTICS_ID = 'UA-112414020-2';
const PROD_GTM_CONTAINER_ID = 'GTM-PT48FVC';
const NON_PROD_GTM_CONTAINER_ID = 'GTM-52DHT8GX';
const ENVIRONMENT = String(process.env.ENVIRONMENT || '').toLowerCase();
const GTM_SCRIPT_ID = 'av-cookie-consent-gtm-script';
const GOOGLE_ANALYTICS_SCRIPT_ID = 'av-cookie-consent-google-analytics-script';
const GOOGLE_ANALYTICS_BOOTSTRAP_KEY = 'avCookieConsentGoogleAnalyticsConfigured';
const CONSENT_DISCLOSURE_COPY = [
  'We use cookies and similar technologies to operate our website,',
  'remember your preferences, measure site traffic and performance, and,',
  'where permitted, support analytics and advertising.',
].join(' ');

const styles = {
  wrap: {
    position: 'fixed',
    right: 0,
    bottom: 8,
    left: 0,
    boxSizing: 'border-box',
    maxWidth: '100vw',
    zIndex: 9999,
    display: 'flex',
    justifyContent: 'center',
    padding: '0 8px',
  },
  banner: {
    width: '100%',
    maxWidth: 1200,
    boxSizing: 'border-box',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    borderRadius: 14,
    background: '#fff',
    boxShadow: '0 10px 26px rgba(15, 23, 42, 0.12)',
    padding: '20px 20px 24px',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: 12,
    rowGap: 10,
  },
  text: {
    margin: 0,
    color: '#4a5565',
    fontSize: 15,
    lineHeight: '22px',
    flex: '1 1 420px',
  },
  policyLink: {
    color: '#7b569b',
    textDecoration: 'underline',
    fontSize: 14,
    fontWeight: 400,
    marginLeft: 6,
  },
  actions: {
    display: 'flex',
    flex: '1 1 220px',
    justifyContent: 'flex-end',
    alignSelf: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  acceptButton: {
    height: 40,
    borderRadius: 10,
    cursor: 'pointer',
    fontSize: 15,
    fontWeight: 650,
    lineHeight: 1,
    border: '1px solid #7b569b',
    background: '#7b569b',
    color: '#fff',
    padding: '0 14px',
    minWidth: 106,
  },
  declineButton: {
    height: 40,
    borderRadius: 10,
    cursor: 'pointer',
    fontSize: 15,
    fontWeight: 650,
    lineHeight: 1,
    border: '1px solid #7b569b',
    background: '#7b569b',
    color: '#fff',
    padding: '0 14px',
    minWidth: 106,
  },
  gotItBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    boxSizing: 'border-box',
    maxWidth: '100vw',
    background: '#eef4fb',
    borderRadius: 12,
    border: '1px solid rgba(0, 0, 0, 0.1)',
    boxShadow: '0 16px 16px -12px rgba(0, 0, 0, 0.12)',
    color: '#0a0a0a',
  },
  gotItArticle: {
    flexGrow: 1,
    minWidth: 0,
  },
  gotItActions: {
    display: 'flex',
    flexShrink: 0,
    alignItems: 'center',
  },
  gotItText: {
    margin: 0,
    fontSize: 15,
    lineHeight: '22px',
    color: '#4a5565',
  },
  gotItButton: {
    height: 40,
    borderRadius: 10,
    cursor: 'pointer',
    fontSize: 15,
    fontWeight: 650,
    lineHeight: 1,
    border: '1px solid #7b569b',
    background: '#7b569b',
    color: '#fff',
    padding: '0 14px',
    minWidth: 106,
    whiteSpace: 'nowrap',
  },
};

const secureSuffix = () => {
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    return '; Secure';
  }

  return '';
};

const readCookie = (cookieName) => {
  if (typeof document === 'undefined') {
    return null;
  }

  const matchingCookie = document.cookie
    .split(';')
    .map((cookiePart) => cookiePart.trim())
    .find((cookiePart) => cookiePart.startsWith(`${cookieName}=`));

  if (matchingCookie) {
    return matchingCookie.slice(cookieName.length + 1);
  }

  return null;
};

const parseRecord = (rawRecordValue) => {
  if (!rawRecordValue) {
    return null;
  }

  try {
    const parsedRecord = JSON.parse(decodeURIComponent(rawRecordValue));

    if (
      !parsedRecord
      || typeof parsedRecord.sessionId !== 'string'
      || (parsedRecord.status !== 'accepted'
        && parsedRecord.status !== 'rejected')
      || typeof parsedRecord.timestamp !== 'string'
    ) {
      return null;
    }

    return {
      sessionId: parsedRecord.sessionId,
      status: parsedRecord.status,
      timestamp: parsedRecord.timestamp,
    };
  } catch (error) {
    return null;
  }
};

const writeRecord = (consentRecord, cookieKey) => {
  if (typeof document === 'undefined') {
    return;
  }

  const expirationTimestamp = new Date(
    Date.now() + ONE_YEAR_SECONDS * 1000,
  ).toUTCString();

  document.cookie = `${cookieKey}=${encodeURIComponent(
    JSON.stringify(consentRecord),
  )}; Path=/; Max-Age=${ONE_YEAR_SECONDS}; Expires=${expirationTimestamp}; SameSite=Lax${secureSuffix()}`;
};

const normalizeGeo = (geoData) => {
  if (!geoData || typeof geoData !== 'object') {
    return null;
  }

  const consentType = typeof geoData.consentType === 'string'
    ? geoData.consentType
    : null;

  if (!consentType || !VALID_CONSENT_TYPES.has(consentType)) {
    return null;
  }

  return { consentType };
};

const readGeoCookie = () => {
  const rawGeoCookie = readCookie(GEO_COOKIE_KEY);

  if (!rawGeoCookie) {
    return null;
  }

  try {
    return normalizeGeo(JSON.parse(decodeURIComponent(rawGeoCookie)));
  } catch (error) {
    return null;
  }
};

const writeGeoCookie = (geo) => {
  if (typeof document === 'undefined') {
    return;
  }

  document.cookie = [
    `${GEO_COOKIE_KEY}=${encodeURIComponent(JSON.stringify(geo))}`,
    'Path=/',
    `Max-Age=${GEO_COOKIE_MAX_AGE}`,
    'SameSite=Lax',
    secureSuffix(),
  ]
    .filter(Boolean)
    .join('; ');
};

const fetchGeo = async (reCaptchaToken) => {
  try {
    return await Promise.race([
      getTrackingConsentType(reCaptchaToken).then((response) => normalizeGeo(response)),
      new Promise((resolve) => {
        setTimeout(() => resolve(null), GEO_FETCH_TIMEOUT_MS);
      }),
    ]);
  } catch (error) {
    return null;
  }
};

const getConsentStatus = (cookieKey) => {
  const consentRecord = parseRecord(readCookie(cookieKey));

  if (consentRecord) {
    return consentRecord.status;
  }

  return 'unknown';
};

const getOrCreateSessionId = (storageKey) => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const existingSessionId = window.sessionStorage.getItem(storageKey);

    if (existingSessionId) {
      return existingSessionId;
    }

    const sessionId = typeof crypto !== 'undefined'
      && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `session-${Date.now()}-${Math.random().toString(16).slice(2)}`;

    window.sessionStorage.setItem(storageKey, sessionId);
    return sessionId;
  } catch (error) {
    return `session-${Date.now()}`;
  }
};

const createConsentRecord = (status) => ({
  sessionId: getOrCreateSessionId(CONSENT_SESSION_STORAGE_KEY),
  status,
  timestamp: new Date().toISOString(),
});

const isGlobalPrivacyControlEnabled = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return (
    window.navigator
    && (window.navigator.globalPrivacyControl === true
      || window.globalPrivacyControl === true)
  );
};

const updateConsentMode = (command, granted) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag(...args) {
    window.dataLayer.push(args);
  };

  const consentValue = granted ? 'granted' : 'denied';

  window.gtag('consent', command, {
    ad_storage: consentValue,
    ad_user_data: consentValue,
    ad_personalization: consentValue,
    analytics_storage: consentValue,
    personalization_storage: consentValue,
    functionality_storage: consentValue,
    security_storage: consentValue,
  });
};

const dispatchConsentEvent = (consentRecord) => {
  if (typeof window === 'undefined') {
    return;
  }

  const consentEventPayload = {
    event: CONSENT_DECISION_EVENT_NAME_IN_GTM,
    session_id: consentRecord.sessionId,
    consent_status: consentRecord.status,
    consent_timestamp: consentRecord.timestamp,
  };

  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push(consentEventPayload);
  }
};

const getGtmContainerId = () => {
  if (ENVIRONMENT === 'prod' || ENVIRONMENT === 'production') {
    return PROD_GTM_CONTAINER_ID;
  }

  return NON_PROD_GTM_CONTAINER_ID;
};

const appendGoogleAnalyticsScript = () => {
  if (!GOOGLE_ANALYTICS_ID || typeof document === 'undefined' || typeof window === 'undefined') {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag(...args) {
    window.dataLayer.push(args);
  };

  if (!window[GOOGLE_ANALYTICS_BOOTSTRAP_KEY]) {
    window.gtag('js', new Date());
    window.gtag('config', GOOGLE_ANALYTICS_ID, {
      send_page_view: false,
    });
    window[GOOGLE_ANALYTICS_BOOTSTRAP_KEY] = true;
  }

  if (document.getElementById(GOOGLE_ANALYTICS_SCRIPT_ID)) {
    return;
  }

  const scriptElement = document.createElement('script');
  scriptElement.id = GOOGLE_ANALYTICS_SCRIPT_ID;
  scriptElement.async = true;
  scriptElement.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_ID}`;
  document.head.appendChild(scriptElement);
};

const trackGoogleAnalyticsPageView = () => {
  if (
    !GOOGLE_ANALYTICS_ID
    || typeof document === 'undefined'
    || typeof window === 'undefined'
    || typeof window.gtag !== 'function'
  ) {
    return;
  }

  const pagePath = `${window.location.pathname}${window.location.search}`;

  window.gtag('config', GOOGLE_ANALYTICS_ID, {
    page_title: document.title,
    page_location: window.location.href,
    page_path: pagePath,
  });
};

const appendGtmScript = () => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return;
  }

  if (document.getElementById(GTM_SCRIPT_ID)) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  const scriptElement = document.createElement('script');
  scriptElement.id = GTM_SCRIPT_ID;
  scriptElement.async = true;
  scriptElement.src = `https://www.googletagmanager.com/gtm.js?id=${getGtmContainerId()}`;
  document.head.appendChild(scriptElement);
};

const grantConsentAndLoadTracking = (
  geo,
  { shouldLoadDirectAnalytics, shouldLoadGtm },
) => {
  if (geo) {
    writeGeoCookie(geo);
  }

  updateConsentMode('update', true);
  if (shouldLoadDirectAnalytics) {
    appendGoogleAnalyticsScript();
  }
  if (shouldLoadGtm) {
    appendGtmScript();
  }
};

const loadAnalyticsGranted = (command = 'default') => {
  updateConsentMode(command, true);
  appendGoogleAnalyticsScript();
};

const loadGtmGranted = () => {
  updateConsentMode('default', true);
  appendGtmScript();
};

const getConsentType = (geo) => {
  if (geo && geo.consentType) {
    return geo.consentType;
  }

  return 'optOut';
};

const requiresConsentBanner = (geo) => {
  const consentType = getConsentType(geo);
  return consentType === 'optIn' || consentType === 'optOut';
};

const shouldLoadTrackingForConsent = (consentStatus, geo) => {
  const consentType = getConsentType(geo);

  if (consentStatus === 'accepted') {
    return true;
  }

  if (consentType === 'acknowledgeOnly') {
    return true;
  }

  return (
    consentType === 'optOut'
    && consentStatus === 'unknown'
    && !isGlobalPrivacyControlEnabled()
  );
};

const sendTrackingConsentReceipt = async ({
  consentRecord,
  consentType,
  decisionSource,
}) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    await logTrackingConsent({
      sessionId: consentRecord.sessionId,
      decision: consentRecord.status,
      consentType,
      decisionSource,
      decisionAt: consentRecord.timestamp,
      path: `${window.location.pathname}${window.location.search}`,
      host: window.location.host,
      userAgent: window.navigator ? window.navigator.userAgent : null,
    });
  } catch (error) {
    // Ignore receipt logging failures so the banner never blocks the page.
  }
};

const TrackingCookieConsent = ({ shouldLoadGtmOnCurrentRoute = false }) => {
  const location = useLocation();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [consentStatus, setConsentStatus] = useState('unknown');
  const [hasResolvedConsent, setHasResolvedConsent] = useState(false);
  const [geo, setGeo] = useState(null);
  const [geoResolved, setGeoResolved] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const geoFetchCancelledRef = useRef(false);
  const lastTrackedDirectGaPathRef = useRef(null);
  const wasDirectGaTrackingEnabledRef = useRef(false);
  const shouldLoadDirectAnalytics = !shouldLoadGtmOnCurrentRoute;

  const applyConsentDecision = useCallback(
    (nextConsentStatus, decisionSource = DEFAULT_DECISION_SOURCE) => {
      const consentRecord = createConsentRecord(nextConsentStatus);
      writeRecord(consentRecord, CONSENT_COOKIE_KEY);

      if (nextConsentStatus === 'accepted') {
        grantConsentAndLoadTracking(geo, {
          shouldLoadDirectAnalytics,
          shouldLoadGtm: shouldLoadGtmOnCurrentRoute,
        });
      } else {
        updateConsentMode('update', false);
      }

      dispatchConsentEvent(consentRecord);
      sendTrackingConsentReceipt({
        consentRecord,
        consentType: getConsentType(geo),
        decisionSource,
      });
      setConsentStatus(nextConsentStatus);
    },
    [geo, shouldLoadDirectAnalytics, shouldLoadGtmOnCurrentRoute],
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mobileMediaQuery = window.matchMedia('(max-width: 767px)');
    const syncMobileState = () => setIsMobile(mobileMediaQuery.matches);
    syncMobileState();
    mobileMediaQuery.addEventListener('change', syncMobileState);

    return () => mobileMediaQuery.removeEventListener('change', syncMobileState);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined' || geoResolved) {
      return undefined;
    }

    document.body.classList.add('recaptcha');

    return () => {
      document.body.classList.remove('recaptcha');
    };
  }, [geoResolved]);

  useEffect(() => {
    const currentConsentStatus = getConsentStatus(CONSENT_COOKIE_KEY);

    if (currentConsentStatus === 'accepted' && shouldLoadDirectAnalytics) {
      loadAnalyticsGranted('update');
    }

    setConsentStatus(currentConsentStatus);
    setHasResolvedConsent(true);
  }, [shouldLoadDirectAnalytics]);

  useEffect(() => {
    if (!hasResolvedConsent || !executeRecaptcha) {
      return undefined;
    }

    if (consentStatus === 'accepted') {
      const cachedGeo = readGeoCookie();

      if (cachedGeo) {
        setGeo(cachedGeo);
        setGeoResolved(true);
        return undefined;
      }
    }

    geoFetchCancelledRef.current = false;

    const resolveGeo = async () => {
      let geoResult = null;

      try {
        const reCaptchaToken = await executeRecaptcha('cookieConsentLocation');

        if (reCaptchaToken) {
          geoResult = await fetchGeo(reCaptchaToken);
        }
      } catch (error) {
        geoResult = null;
      }

      if (geoFetchCancelledRef.current) {
        return;
      }

      if (geoResult) {
        writeGeoCookie(geoResult);
      }

      setGeo(geoResult);
      setGeoResolved(true);
    };

    resolveGeo();

    return () => {
      geoFetchCancelledRef.current = true;
    };
  }, [consentStatus, executeRecaptcha, hasResolvedConsent]);

  useEffect(() => {
    if (!hasResolvedConsent || !geoResolved || consentStatus !== 'unknown') {
      return;
    }

    if (getConsentType(geo) !== 'optOut') {
      return;
    }

    if (!isGlobalPrivacyControlEnabled()) {
      return;
    }

    applyConsentDecision('rejected', 'gpc');
  }, [
    applyConsentDecision,
    consentStatus,
    geo,
    geoResolved,
    hasResolvedConsent,
  ]);

  useEffect(() => {
    if (
      !hasResolvedConsent
      || !geoResolved
      || !shouldLoadDirectAnalytics
    ) {
      return;
    }

    if (!shouldLoadTrackingForConsent(consentStatus, geo)) {
      return;
    }

    const command = consentStatus === 'accepted' ? 'update' : 'default';
    loadAnalyticsGranted(command);
  }, [consentStatus, geo, geoResolved, hasResolvedConsent, shouldLoadDirectAnalytics]);

  useEffect(() => {
    if (
      !hasResolvedConsent
      || !geoResolved
      || !shouldLoadDirectAnalytics
      || !shouldLoadTrackingForConsent(consentStatus, geo)
    ) {
      wasDirectGaTrackingEnabledRef.current = false;
      return;
    }

    const currentPagePath = `${window.location.pathname}${window.location.search}`;
    const shouldTrackPageView = (
      currentPagePath !== lastTrackedDirectGaPathRef.current
      || !wasDirectGaTrackingEnabledRef.current
    );

    if (!shouldTrackPageView) {
      wasDirectGaTrackingEnabledRef.current = true;
      return;
    }

    trackGoogleAnalyticsPageView();
    lastTrackedDirectGaPathRef.current = currentPagePath;
    wasDirectGaTrackingEnabledRef.current = true;
  }, [
    consentStatus,
    geo,
    geoResolved,
    hasResolvedConsent,
    location.pathname,
    location.search,
    shouldLoadDirectAnalytics,
  ]);

  useEffect(() => {
    if (!hasResolvedConsent || !geoResolved || !shouldLoadGtmOnCurrentRoute) {
      return;
    }

    const consentType = getConsentType(geo);

    if (consentStatus === 'accepted') {
      updateConsentMode('update', true);
      appendGtmScript();
      return;
    }

    if (consentType === 'acknowledgeOnly') {
      loadGtmGranted();
      return;
    }

    if (consentType !== 'optOut') {
      return;
    }

    if (isGlobalPrivacyControlEnabled()) {
      return;
    }

    if (consentStatus === 'unknown') {
      loadGtmGranted();
    }
  }, [consentStatus, geo, geoResolved, hasResolvedConsent, shouldLoadGtmOnCurrentRoute]);

  if (!hasResolvedConsent || !geoResolved || consentStatus !== 'unknown') {
    return null;
  }

  if (!requiresConsentBanner(geo)) {
    const gotItBannerStyle = isMobile
      ? {
        ...styles.gotItBanner,
        position: 'fixed',
        bottom: 8,
        right: 8,
        left: 8,
        zIndex: 9999,
        padding: '16px',
        flexDirection: 'column',
        alignItems: 'stretch',
      }
      : {
        ...styles.gotItBanner,
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 9999,
        padding: '14px 16px 14px 20px',
        width: 460,
        maxWidth: 'calc(100vw - 48px)',
      };

    const gotItArticleStyle = isMobile
      ? { ...styles.gotItArticle, paddingTop: 0 }
      : { ...styles.gotItArticle, marginRight: 8, paddingTop: 0 };
    const gotItButtonStyle = isMobile
      ? {
        ...styles.gotItButton, width: '100%', minWidth: 0, paddingTop: 0,
      }
      : styles.gotItButton;

    return createPortal(
      <div style={gotItBannerStyle}>
        <article style={gotItArticleStyle}>
          <p style={styles.gotItText}>
            This site uses cookies.{' '}
            <a
              href={DEFAULT_COOKIE_POLICY_URL}
              target="_blank"
              rel="noreferrer"
              style={styles.policyLink}
            >
              Read more about cookies.
            </a>
          </p>
        </article>
        <nav style={styles.gotItActions}>
          <button
            type="button"
            style={gotItButtonStyle}
            onClick={() => applyConsentDecision('accepted')}
          >
            Got it
          </button>
        </nav>
      </div>,
      document.body,
    );
  }

  const wrapStyle = isMobile
    ? {
      ...styles.wrap,
      left: 8,
      right: 8,
      bottom: 8,
      padding: 0,
    }
    : styles.wrap;
  const textStyle = isMobile
    ? { ...styles.text, flex: '1 1 100%' }
    : styles.text;
  const actionsStyle = isMobile
    ? {
      ...styles.actions,
      flex: '1 1 100%',
      justifyContent: 'stretch',
      flexDirection: 'column',
      marginTop: 8,
    }
    : styles.actions;
  const acceptButtonStyle = isMobile
    ? { ...styles.acceptButton, width: '100%', minWidth: 0 }
    : styles.acceptButton;
  const declineButtonStyle = isMobile
    ? { ...styles.declineButton, width: '100%', minWidth: 0 }
    : styles.declineButton;

  return createPortal(
    <div style={wrapStyle}>
      <div style={styles.banner}>
        <p style={textStyle}>
          {CONSENT_DISCLOSURE_COPY}{' '}
          <a
            href={DEFAULT_COOKIE_POLICY_URL}
            target="_blank"
            rel="noreferrer"
            style={styles.policyLink}
          >
            See our Cookie Policy
          </a>{' '}
          for more information.
        </p>
        <div style={actionsStyle}>
          <button
            type="button"
            style={acceptButtonStyle}
            onClick={() => applyConsentDecision('accepted')}
          >
            Accept All Cookies
          </button>
          <button
            type="button"
            style={declineButtonStyle}
            onClick={() => applyConsentDecision('rejected')}
          >
            Reject Nonessential Cookies
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

TrackingCookieConsent.propTypes = {
  shouldLoadGtmOnCurrentRoute: PropTypes.bool,
};

TrackingCookieConsent.defaultProps = {
  shouldLoadGtmOnCurrentRoute: false,
};

export default TrackingCookieConsent;
